import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { boatApi } from '../services/boatApi';
import type { BoatFormData } from '../types/boat.types';

// Query keys
export const boatKeys = {
  all: ['boats'] as const,
  lists: () => [...boatKeys.all, 'list'] as const,
  list: (filters: Record<string, any>) => [...boatKeys.lists(), filters] as const,
  details: () => [...boatKeys.all, 'detail'] as const,
  detail: (id: number | string) => [...boatKeys.details(), id] as const,
};

/**
 * Hook to fetch boats list with filters
 * CRITICAL: Filters must be memoized in parent component to prevent infinite re-renders
 *
 * `enabled` lets callers gate the query on a precondition (e.g. DraftPicker
 * waits for the Draft system status id to resolve before listing drafts).
 */
export function useBoats(filters: Record<string, any>, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: boatKeys.list(filters),
    queryFn: () => boatApi.getBoats(filters),
    enabled: options?.enabled ?? true,
  });
}

/**
 * Hook to fetch single boat
 */
export function useBoat(id: number | string) {
  return useQuery({
    queryKey: boatKeys.detail(id),
    queryFn: () => boatApi.getBoat(id),
    enabled: !!id,
  });
}

/**
 * Hook to create boat
 */
export function useCreateBoat() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BoatFormData) => boatApi.createBoat(data),
    onSuccess: () => {
      // Invalidate and refetch boats list
      queryClient.invalidateQueries({ queryKey: boatKeys.lists() });
    },
  });
}

/**
 * Hook to update boat
 */
export function useUpdateBoat() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number | string; data: Partial<BoatFormData> }) =>
      boatApi.updateBoat(id, data),
    onSuccess: (_, variables) => {
      // Invalidate specific boat and list
      queryClient.invalidateQueries({ queryKey: boatKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: boatKeys.lists() });
    },
  });
}

/**
 * SP2-C — boat asset-link mutations (attach / detach / swap an engine or
 * trailer). Each invalidates the boat detail + boats/engines/trailers lists
 * so the attached-assets section, list pages, and asset detail pages all
 * refresh (an asset's current_boat / attachment facet changes too).
 */
function useAssetLinkInvalidation(boatId: string) {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: boatKeys.detail(boatId) });
    queryClient.invalidateQueries({ queryKey: boatKeys.lists() });
    queryClient.invalidateQueries({ queryKey: ['engines'] });
    queryClient.invalidateQueries({ queryKey: ['trailers'] });
  };
}

export function useAttachAsset(boatId: string) {
  const invalidate = useAssetLinkInvalidation(boatId);

  return useMutation({
    mutationFn: (assetId: string) => boatApi.attachAsset(boatId, assetId),
    onSuccess: invalidate,
  });
}

export function useDetachAsset(boatId: string) {
  const invalidate = useAssetLinkInvalidation(boatId);

  return useMutation({
    mutationFn: (assetId: string) => boatApi.detachAsset(boatId, assetId),
    onSuccess: invalidate,
  });
}

export function useSwapAsset(boatId: string) {
  const invalidate = useAssetLinkInvalidation(boatId);

  return useMutation({
    mutationFn: ({ assetId, replacementAssetId }: { assetId: string; replacementAssetId: string }) =>
      boatApi.swapAsset(boatId, assetId, replacementAssetId),
    onSuccess: invalidate,
  });
}

/**
 * Hook to delete boat
 */
export function useDeleteBoat() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number | string) => boatApi.deleteBoat(id),
    onSuccess: () => {
      // Invalidate boats list
      queryClient.invalidateQueries({ queryKey: boatKeys.lists() });
    },
  });
}

/**
 * #607 — publish a boat to the storefront.
 *
 * Deliberately NOT `useUpdateBoat({ website_status: 'published' })`: a partial
 * PUT runs the server's buildItemData(), which defaults `featured` and
 * `is_on_sale` to false and clears both on any body that omits them. The
 * dedicated endpoint writes only website_status.
 */
export function usePublishBoatToWebsite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number | string) => boatApi.publishToWebsite(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: boatKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: boatKeys.lists() });
    },
  });
}

/**
 * MutationKey factory for `markBoatAsSold`.
 *
 * CR-07: parent (`BoatPublishingCard`) and child (`MarkSoldDialog`) each call
 * `useMarkBoatAsSold()` independently — without a shared `mutationKey` they
 * produce two isolated mutation observers and the parent's `isPending` never
 * reflects the dialog's in-flight state. Subscribers should observe the
 * shared state via `useIsMutating({ mutationKey: markBoatAsSoldMutationKey(boatId) })`
 * rather than reading `isPending` from a fresh `useMarkBoatAsSold()` call.
 *
 * Tuple shape `['markBoatAsSold', boatId]` keeps the lock per-boat — concurrent
 * mark-sold flows on different boats (rare but possible from a list view)
 * don't cross-lock each other.
 */
export const markBoatAsSoldMutationKey = (boatId: number | string) =>
  ['markBoatAsSold', boatId] as const;

/**
 * Hook to mark boat as sold
 */
export function useMarkBoatAsSold(boatId?: number | string) {
  const queryClient = useQueryClient();

  return useMutation({
    // CR-07: Per-boat mutationKey lets sibling components observe the same
    // mutation via `useIsMutating({ mutationKey: markBoatAsSoldMutationKey(boatId) })`.
    // When `boatId` is omitted (legacy call sites that only invoke the
    // mutation and don't need cross-component observation), the key is still
    // attached but with an undefined second slot — useIsMutating queries with
    // a concrete boatId won't match it, which is the correct behavior.
    mutationKey: markBoatAsSoldMutationKey(boatId ?? ''),
    mutationFn: ({
      id,
      saleData,
    }: {
      id: number | string;
      saleData: {
        sold_price?: number;
        sold_date?: string;
        sold_to_customer_id?: string;
        // Phase 50: hide_from_website. Default true server-side per Plan 50-02.
        hide_from_website?: boolean;
      };
    }) => boatApi.markAsSold(id, saleData),
    onSuccess: (_, variables) => {
      // CR-07: Return a Promise that resolves when the invalidations complete.
      // TanStack Query keeps the mutation in 'pending' state until this
      // Promise settles — meaning `useIsMutating` returns > 0 for the whole
      // mutate-then-refetch window. The MarkSoldDialog's `mutateAsync`
      // similarly awaits this Promise before resolving, so when the dialog
      // calls `onOpenChange(false)` after `await mutateAsync`, the boat
      // detail query has already been refetched with the new status_id.
      // Without this return, the mutation settles immediately after the API
      // call resolves and the lock disengages before the refetch lands —
      // the exact race CR-07 / CR-04 targeted.
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: boatKeys.detail(variables.id) }),
        queryClient.invalidateQueries({ queryKey: boatKeys.lists() }),
      ]);
    },
  });
}
